import { createInsertSchema, createSelectSchema } from "drizzle-zod";
import { z } from "zod";
import { isRfc3339WithOffset, rfc3339ToDate } from "@workspace/datetime";
import { steps } from "../drizzle/steps.js";
import { stepTypeSchema } from "./stepTypes.js";
export const stepSchema = createSelectSchema(steps, {
    id: (schema) => schema.meta({
        description: "Step ID",
        example: 1,
    }),
    // Use z.coerce.date() for date fields because JSON serialization converts Date objects to ISO strings
    createdAt: () => z.coerce.date().meta({
        description: "Step creation timestamp",
        example: "2024-01-15T10:30:00.000Z",
    }),
    updatedAt: () => z.coerce.date().meta({
        description: "Step last update timestamp",
        example: "2024-01-15T10:30:00.000Z",
    }),
    type: (schema) => schema.meta({
        description: "Step type key (references stepTypes.key)",
        example: "cost_estimate_check",
    }),
    status: (schema) => schema.meta({
        description: "Step status",
        example: "success",
    }),
    task: (schema) => schema.meta({
        description: "Task ID that this step belongs to",
        example: 1,
    }),
    title: (schema) => schema.meta({
        description: "Step title",
        example: "Upload Invoice",
    }),
    input: (schema) => schema.meta({
        description: "Step input data from previous step (JSON)",
        example: { schadennummer: "2024-123456" },
    }),
    result: (schema) => schema.meta({
        description: "Step result data (JSON)",
        example: { approved: true, notes: "Looks good" },
    }),
    startTime: () => z.coerce.date().nullable().meta({
        description: "Actual start time of the step",
        example: "2024-01-15T10:30:00.000Z",
    }),
    endTime: () => z.coerce.date().nullable().meta({
        description: "Actual end time of the step",
        example: "2024-01-15T10:45:00.000Z",
    }),
}).describe("Step object");
// Base schema using callbacks - stays in sync with Drizzle column types
const baseCreateStepSchema = createInsertSchema(steps, {
    type: (schema) => schema.meta({
        description: "Step type key (references stepTypes.key)",
        example: "cost_estimate_check",
    }),
    status: (schema) => schema.meta({
        description: "Step status",
        example: "success",
    }),
    task: () => z
        .preprocess((val) => (Number.isNaN(val) ? undefined : val), z.number({ message: "Task ist ein Pflichtfeld" }).min(1, "Die Task-ID muss eine positive Zahl sein"))
        .meta({
        description: "Task ID that this step belongs to",
        example: 1,
    }),
    title: (schema) => schema.min(1, "Bitte gib einen Titel an").meta({
        description: "Step title",
        example: "Upload Invoice",
    }),
    input: (schema) => schema
        .meta({
        description: "Step input data from previous step (JSON)",
        example: { schadennummer: "2024-123456" },
    })
        .nullable(),
    result: (schema) => schema
        .meta({
        description: "Step result data (JSON)",
        example: { approved: true, notes: "Looks good" },
    })
        .nullable(),
    startTime: () => z
        .string()
        .refine(isRfc3339WithOffset, "Expected RFC 3339 datetime with offset")
        .transform((val) => rfc3339ToDate(val))
        .nullable()
        .meta({
        description: "Actual start time (RFC 3339 with offset)",
        example: "2026-01-15T10:30:00+01:00",
    }),
    endTime: () => z
        .string()
        .refine(isRfc3339WithOffset, "Expected RFC 3339 datetime with offset")
        .transform((val) => rfc3339ToDate(val))
        .nullable()
        .meta({
        description: "Actual end time (RFC 3339 with offset)",
        example: "2026-01-15T10:45:00+01:00",
    }),
});
// Fix drizzle-zod bug: callbacks for nullable fields lose .optional() wrapper
// We extend the base schema to add .optional() to nullable fields
export const createStepSchema = baseCreateStepSchema
    .extend({
    input: baseCreateStepSchema.shape.input.optional(),
    result: baseCreateStepSchema.shape.result.optional(),
    startTime: baseCreateStepSchema.shape.startTime.optional(),
    endTime: baseCreateStepSchema.shape.endTime.optional(),
})
    .describe("Create step request body");
export const stepWithTypeSchema = stepSchema.extend({
    stepTypeInfo: stepTypeSchema,
});
